import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Camera, Banknote, Smartphone, Tag, Plus, Package, RefreshCw, Trash2 } from 'lucide-react';
import api from '../../api';
import { buildCategoryOptions, categoryDepth, MAX_CATEGORY_DEPTH } from '../../utils/categoryTree';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const EMPTY_ITEM = () => ({
  mode: 'new', // 'new' | 'existing'
  existing_stock_id: '',
  product_name: '', description: '', category_id: '', quantity: '', unit_cost: '', public_price: '', photo: null,
});

const SHARED_FORM = {
  provider_id: '', shipping_cost: '', status: 'pending', paid_amount: '', payment_method: '', notes: '', invoice_photo: null,
};

export default function VendorProductWizard({ user, providers, onClose, onProviderCreated, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [items, setItems] = useState([EMPTY_ITEM()]);
  const [form, setForm] = useState(SHARED_FORM);
  const [formNewProvider, setFormNewProvider] = useState('');
  const [showNewProviderInline, setShowNewProviderInline] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState({ forIndex: null, name: '', parent_id: '' });
  const [savingCategory, setSavingCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const invoiceInputRef = useRef(null);
  const itemPhotoRefs = useRef({});

  const loadCategories = useCallback(async () => {
    try { const r = await api.get(`/catalog-categories/${user.id}`); setCategories(r.data); } catch (e) { console.error(e); }
  }, [user.id]);

  const loadExistingProducts = useCallback(async () => {
    try { const r = await api.get(`/vendor-products/${user.id}`); setExistingProducts(r.data); } catch (e) { console.error(e); }
  }, [user.id]);

  useEffect(() => { loadCategories(); loadExistingProducts(); }, [loadCategories, loadExistingProducts]);

  const categoryOptions = buildCategoryOptions(categories);

  // ── Items list ──
  const addItem = () => setItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));

  const switchItemMode = (idx, mode) => setItems(prev => prev.map((it, i) => i === idx ? { ...EMPTY_ITEM(), mode } : it));

  const selectExistingForItem = (idx, stockId) => {
    const p = existingProducts.find(x => x.stock_id === stockId);
    setItems(prev => prev.map((it, i) => i === idx ? {
      ...it,
      existing_stock_id: stockId,
      product_name: p?.name || '',
      description: p?.description || '',
      category_id: p?.category_id || '',
      unit_cost: p?.unit_cost != null ? Number(p.unit_cost).toFixed(2) : '',
      public_price: p?.public_price != null ? Number(p.public_price).toFixed(2) : '',
      photo: p?.photo || null,
    } : it));
  };

  const handleItemPhoto = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => updateItem(idx, 'photo', ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleInvoicePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, invoice_photo: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const createInlineProvider = async () => {
    if (!formNewProvider.trim()) return;
    setSavingProvider(true);
    try {
      const r = await api.post('/providers', { business_user_id: user.id, name: formNewProvider.trim() });
      onProviderCreated(r.data);
      setForm(f => ({ ...f, provider_id: r.data.id }));
      setFormNewProvider('');
      setShowNewProviderInline(false);
    } catch { alert('Error al crear proveedor'); }
    finally { setSavingProvider(false); }
  };

  const openCategoryDraft = (idx) => setCategoryDraft({ forIndex: idx, name: '', parent_id: '' });
  const closeCategoryDraft = () => setCategoryDraft({ forIndex: null, name: '', parent_id: '' });

  const createCategoryFromDraft = async () => {
    if (!categoryDraft.name.trim()) return;
    setSavingCategory(true);
    try {
      const r = await api.post('/catalog-categories', {
        business_user_id: user.id,
        name: categoryDraft.name.trim(),
        parent_id: categoryDraft.parent_id || null,
      });
      setCategories(prev => [...prev, r.data]);
      updateItem(categoryDraft.forIndex, 'category_id', r.data.id);
      closeCategoryDraft();
    } catch (err) { alert(err.response?.data?.detail || 'Error al crear categoría'); }
    finally { setSavingCategory(false); }
  };

  // ── Totals ──
  const totalQuantity = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
  const shippingCost = parseFloat(form.shipping_cost) || 0;
  const shippingPerUnit = totalQuantity > 0 ? shippingCost / totalQuantity : 0;
  const itemsCost = items.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0)), 0);
  const totalCost = itemsCost + shippingCost;

  const save = async (e) => {
    e.preventDefault();
    if (!form.provider_id) { alert('Seleccioná un proveedor'); return; }
    if (items.length === 0) { alert('Agregá al menos un producto'); return; }
    for (const it of items) {
      if (it.mode === 'new' && !it.product_name.trim()) { alert('Ingresá el nombre de todos los productos nuevos'); return; }
      if (it.mode === 'existing' && !it.existing_stock_id) { alert('Seleccioná el producto existente a actualizar'); return; }
      if (!parseFloat(it.quantity) || parseFloat(it.quantity) <= 0) { alert('Ingresá una cantidad válida en todos los productos'); return; }
      if (it.unit_cost === '' || parseFloat(it.unit_cost) < 0) { alert('Ingresá el precio unitario en todos los productos'); return; }
      if (!parseFloat(it.public_price) || parseFloat(it.public_price) <= 0) { alert('Ingresá el precio al público en todos los productos'); return; }
    }
    if ((form.status === 'paid' || form.status === 'partial') && !form.payment_method) { alert('Seleccioná la forma de pago'); return; }

    setSaving(true);
    try {
      await api.post('/vendor-product-load', {
        business_user_id: user.id,
        provider_id: form.provider_id,
        items: items.map(it => {
          const existing = it.mode === 'existing' ? existingProducts.find(p => p.stock_id === it.existing_stock_id) : null;
          return {
            product_name: it.mode === 'existing' ? existing?.name : it.product_name.trim(),
            description: it.description ? it.description.trim() : null,
            category_id: it.category_id || null,
            quantity: parseFloat(it.quantity),
            unit_cost: parseFloat(it.unit_cost),
            public_price: parseFloat(it.public_price),
            photo: it.photo || null,
            existing_stock_id: it.mode === 'existing' ? it.existing_stock_id : null,
            existing_catalog_product_id: it.mode === 'existing' ? (existing?.catalog_product_id || null) : null,
            existing_dashboard_product_id: it.mode === 'existing' ? (existing?.dashboard_product_id || null) : null,
          };
        }),
        shipping_cost: shippingCost,
        status: form.status,
        paid_amount: form.status === 'partial' ? (parseFloat(form.paid_amount) || 0) : 0,
        payment_method: (form.status === 'paid' || form.status === 'partial') ? form.payment_method : null,
        notes: form.notes || null,
        invoice_photo: form.invoice_photo || null,
      });
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error al cargar los productos'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflowY: 'auto', maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="modal-title" style={{ margin: 0 }}>Cargar Productos del Pedido</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
            <X size={22} />
          </button>
        </div>

        <form onSubmit={save}>
          {/* Provider (shared) */}
          <div className="form-group">
            <label className="label">Proveedor *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="input"
                data-testid="wizard-provider-select"
                value={form.provider_id}
                onChange={e => setForm(f => ({ ...f, provider_id: e.target.value }))}
                style={{ flex: 1 }}
              >
                <option value="">Seleccionar proveedor</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button type="button" className="btn-secondary" style={{ padding: '8px 12px', whiteSpace: 'nowrap', fontSize: '12px' }} onClick={() => setShowNewProviderInline(v => !v)}>
                + Nuevo
              </button>
            </div>
            {showNewProviderInline && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <input className="input" placeholder="Nombre del proveedor" data-testid="wizard-inline-provider-name" value={formNewProvider} onChange={e => setFormNewProvider(e.target.value)} style={{ flex: 1, minWidth: 0, border: '1px solid var(--accent)' }} />
                <button type="button" data-testid="wizard-inline-provider-create" onClick={createInlineProvider} disabled={savingProvider} style={{ padding: '10px 16px', borderRadius: 10, flexShrink: 0, background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))', color: '#000', fontWeight: 700, fontSize: '13px', border: 'none', cursor: 'pointer' }}>
                  {savingProvider ? '...' : 'Crear'}
                </button>
              </div>
            )}
          </div>

          {/* Items list */}
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)', margin: '18px 0 10px' }}>
            Lista de productos del pedido ({items.length})
          </div>

          {items.map((it, idx) => {
            const qty = parseFloat(it.quantity) || 0;
            const unitCost = parseFloat(it.unit_cost) || 0;
            const realUnitCost = unitCost + shippingPerUnit;
            const publicPrice = parseFloat(it.public_price) || 0;
            return (
              <div key={idx} data-testid={`wizard-item-${idx}`} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 14, background: 'var(--surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)' }}>Producto #{idx + 1}</span>
                  {items.length > 1 && (
                    <button type="button" data-testid={`wizard-remove-item-${idx}`} onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4 }}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>

                {/* Mode toggle */}
                {existingProducts.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    <button type="button" data-testid={`wizard-item-mode-new-${idx}`} onClick={() => switchItemMode(idx, 'new')} style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: `2px solid ${it.mode === 'new' ? 'var(--accent)' : 'var(--border)'}`, background: it.mode === 'new' ? 'rgba(93,230,200,0.1)' : 'var(--bg)', color: it.mode === 'new' ? 'var(--accent)' : 'var(--text2)', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <Plus size={12} /> Nuevo
                    </button>
                    <button type="button" data-testid={`wizard-item-mode-existing-${idx}`} onClick={() => switchItemMode(idx, 'existing')} style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: `2px solid ${it.mode === 'existing' ? 'var(--accent)' : 'var(--border)'}`, background: it.mode === 'existing' ? 'rgba(93,230,200,0.1)' : 'var(--bg)', color: it.mode === 'existing' ? 'var(--accent)' : 'var(--text2)', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <RefreshCw size={12} /> Reponer stock
                    </button>
                  </div>
                )}

                {it.mode === 'existing' && (
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="label" style={{ fontSize: '12px' }}>Producto existente *</label>
                    <select className="input" data-testid={`wizard-existing-product-select-${idx}`} value={it.existing_stock_id} onChange={e => selectExistingForItem(idx, e.target.value)}>
                      <option value="">Seleccionar producto</option>
                      {existingProducts.map(p => <option key={p.stock_id} value={p.stock_id}>{p.name} — Stock: {p.quantity}</option>)}
                    </select>
                  </div>
                )}

                {/* Photo */}
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Camera size={12} /> Foto (catálogo + botón de venta)
                  </label>
                  <input type="file" ref={el => (itemPhotoRefs.current[idx] = el)} accept="image/*" capture="environment" onChange={e => handleItemPhoto(idx, e)} style={{ display: 'none' }} data-testid={`wizard-item-photo-input-${idx}`} />
                  {!it.photo ? (
                    <button type="button" className="btn-secondary" data-testid={`wizard-item-add-photo-${idx}`} onClick={() => itemPhotoRefs.current[idx]?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '12px', padding: '8px' }}>
                      <Camera size={14} /> Sacar / cargar foto
                    </button>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img src={it.photo} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn-secondary" style={{ fontSize: '11px', padding: '5px 8px' }} onClick={() => itemPhotoRefs.current[idx]?.click()}>Cambiar</button>
                        <button type="button" data-testid={`wizard-item-remove-photo-${idx}`} style={{ fontSize: '11px', padding: '5px 8px', borderRadius: 6, background: 'none', border: '1px solid var(--danger)', color: 'var(--danger)', cursor: 'pointer' }} onClick={() => updateItem(idx, 'photo', null)}>
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Name (new only) */}
                {it.mode === 'new' && (
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="label" style={{ fontSize: '12px' }}>Nombre del producto *</label>
                    <input className="input" data-testid={`wizard-item-name-${idx}`} value={it.product_name} onChange={e => updateItem(idx, 'product_name', e.target.value)} placeholder="Ej: Auriculares Bluetooth" />
                  </div>
                )}

                {/* Description (optional, shown in the public catalog) */}
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: '12px' }}>Descripción (opcional, se muestra en el catálogo)</label>
                  <textarea className="input" rows={2} data-testid={`wizard-item-description-${idx}`} value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Características, marca, talles, colores..." />
                </div>

                {/* Category (hierarchical) */}
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: '12px' }}><Tag size={12} style={{ marginRight: 4 }} /> Categoría / Subcategoría</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select className="input" data-testid={`wizard-item-category-${idx}`} value={it.category_id} onChange={e => updateItem(idx, 'category_id', e.target.value)} style={{ flex: 1 }}>
                      <option value="">Sin categoría</option>
                      {categoryOptions.map(c => <option key={c.id} value={c.id}>{'—'.repeat(c.depth)} {c.name}</option>)}
                    </select>
                    <button type="button" className="btn-secondary" style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: '11px' }} onClick={() => openCategoryDraft(idx)}>
                      + Nueva
                    </button>
                  </div>
                  {categoryDraft.forIndex === idx && (
                    <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--accent)', borderRadius: 8 }}>
                      <input className="input" placeholder="Nombre de categoría/subcategoría" data-testid={`wizard-inline-category-name-${idx}`} value={categoryDraft.name} onChange={e => setCategoryDraft(d => ({ ...d, name: e.target.value }))} style={{ marginBottom: 8 }} />
                      <select className="input" data-testid={`wizard-inline-category-parent-${idx}`} value={categoryDraft.parent_id} onChange={e => setCategoryDraft(d => ({ ...d, parent_id: e.target.value }))} style={{ marginBottom: 8 }}>
                        <option value="">Categoría principal (sin padre)</option>
                        {categoryOptions.filter(c => categoryDepth(categories, c.id) <= MAX_CATEGORY_DEPTH).map(c => (
                          <option key={c.id} value={c.id}>{'—'.repeat(c.depth)} {c.name}</option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn-secondary" style={{ flex: 1, fontSize: '12px' }} onClick={closeCategoryDraft}>Cancelar</button>
                        <button type="button" data-testid={`wizard-inline-category-create-${idx}`} onClick={createCategoryFromDraft} disabled={savingCategory} style={{ flex: 1, padding: '8px', borderRadius: 8, background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))', color: '#000', fontWeight: 700, fontSize: '12px', border: 'none', cursor: 'pointer' }}>
                          {savingCategory ? '...' : 'Crear'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quantity / cost / price */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label className="label" style={{ fontSize: '11px' }}>Cantidad *</label>
                    <input className="input" type="number" min="0" step="1" data-testid={`wizard-item-quantity-${idx}`} value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="label" style={{ fontSize: '11px' }}>Precio Unit. *</label>
                    <input className="input" type="number" min="0" step="any" data-testid={`wizard-item-unit-cost-${idx}`} value={it.unit_cost} onChange={e => updateItem(idx, 'unit_cost', e.target.value)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="label" style={{ fontSize: '11px' }}>Precio Público *</label>
                    <input className="input" type="number" min="0" step="any" data-testid={`wizard-item-public-price-${idx}`} value={it.public_price} onChange={e => updateItem(idx, 'public_price', e.target.value)} placeholder="0.00" />
                  </div>
                </div>

                {qty > 0 && unitCost >= 0 && (
                  <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 4 }}>
                    {shippingCost > 0 && <>Costo real (con envío): <strong style={{ color: 'var(--text)' }}>{fmt(realUnitCost)}</strong>/u · </>}
                    {publicPrice > 0 && (
                      <span style={{ color: (publicPrice - realUnitCost) < 0 ? '#FF5252' : 'var(--accent)' }}>
                        Ganancia/u: {fmt(publicPrice - realUnitCost)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <button type="button" className="btn-secondary" data-testid="wizard-add-item-btn" onClick={addItem} style={{ width: '100%', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Plus size={15} /> Agregar otro producto
          </button>

          {/* Shipping cost (shared) */}
          <div className="form-group">
            <label className="label">Gasto de envío del pedido (opcional)</label>
            <input className="input" type="number" min="0" step="0.01" data-testid="wizard-shipping-cost" value={form.shipping_cost} onChange={e => setForm(f => ({ ...f, shipping_cost: e.target.value }))} placeholder="0.00" />
          </div>

          {/* Totals preview */}
          {totalQuantity > 0 && (
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '12px', color: 'var(--text2)' }}>
              <div>Total de unidades: <strong style={{ color: 'var(--text)' }}>{totalQuantity}</strong></div>
              {shippingCost > 0 && <div>Envío por unidad: <strong style={{ color: '#FF9800' }}>{fmt(shippingPerUnit)}</strong></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span>Total del pedido</span>
                <span style={{ fontWeight: 800, color: '#27AE60', fontSize: '16px' }}>{fmt(totalCost)}</span>
              </div>
            </div>
          )}

          {/* Payment status */}
          <div className="form-group">
            <label className="label">Estado de pago *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { key: 'paid', label: 'Pagado', color: '#4CAF50' },
                { key: 'partial', label: 'Parcial', color: '#FF9800' },
                { key: 'pending', label: 'Pendiente', color: '#FF5252' },
              ].map(s => (
                <button key={s.key} type="button" data-testid={`wizard-status-${s.key}`} onClick={() => setForm(f => ({ ...f, status: s.key }))} style={{ flex: 1, padding: '10px 4px', borderRadius: 10, border: `2px solid ${form.status === s.key ? s.color : 'var(--border)'}`, background: form.status === s.key ? `${s.color}20` : 'var(--surface)', color: form.status === s.key ? s.color : 'var(--text2)', fontWeight: form.status === s.key ? 700 : 500, fontSize: '13px', cursor: 'pointer' }}>
                  {s.label}
                </button>
              ))}
            </div>
            {form.status === 'partial' && (
              <div style={{ marginTop: 10 }}>
                <label className="label">Monto ya pagado</label>
                <input className="input" type="number" step="0.01" value={form.paid_amount} onChange={e => setForm(f => ({ ...f, paid_amount: e.target.value }))} data-testid="wizard-paid-amount" />
              </div>
            )}
            {(form.status === 'paid' || form.status === 'partial') && (
              <div style={{ marginTop: 12 }}>
                <label className="label">Forma de pago *</label>
                <div className="payment-row">
                  <button type="button" data-testid="wizard-pay-efectivo" className={`payment-btn ${form.payment_method === 'efectivo' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, payment_method: 'efectivo' }))}>
                    <Banknote size={16} /> Efectivo
                  </button>
                  <button type="button" data-testid="wizard-pay-transferencia" className={`payment-btn ${form.payment_method === 'transferencia' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, payment_method: 'transferencia' }))}>
                    <Smartphone size={16} /> Transferencia
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="label">Notas (opcional)</label>
            <input className="input" data-testid="wizard-notes" placeholder="Observaciones del pedido..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {/* Invoice photo */}
          <div className="form-group">
            <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={14} /> Foto de factura / comprobante (opcional)
            </label>
            <input type="file" ref={invoiceInputRef} accept="image/*" onChange={handleInvoicePhoto} style={{ display: 'none' }} data-testid="wizard-invoice-input" />
            {!form.invoice_photo ? (
              <button type="button" className="btn-secondary" data-testid="wizard-add-invoice-btn" onClick={() => invoiceInputRef.current?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Camera size={15} /> Cargar foto de factura
              </button>
            ) : (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={form.invoice_photo} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                <button type="button" onClick={() => setForm(f => ({ ...f, invoice_photo: null }))} style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            )}
          </div>

          <div style={{ background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: '12px', color: 'var(--text2)' }}>
            Al registrar, cada producto de la lista creará/actualizará automáticamente su stock, su producto del catálogo y su botón de venta en la página principal.
          </div>

          <div className="modal-buttons">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" data-testid="wizard-save-btn" disabled={saving}>
              {saving ? 'Registrando...' : `Registrar ${items.length} Producto(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
